import React, { useCallback, useState } from "react";
import { Button, Card, Input, Modal, Spin } from "antd";
import { useParams } from "react-router-dom";
import { useGetDocumentsMutation } from "../../../store/api/lumi/documents";
import { LoadingOutlined } from "@ant-design/icons";
import { getFormattedDocument } from "../../../utils/Docs/DocUtils";
import { ParagraphList } from "./ParagraphList";
import Scrollbars from "react-custom-scrollbars-2";
import MetadataFields from "./MetadataFields";
import { useGetLLMPassThroughMutation } from "../../../store/api/lumi/llm";

const SentimentDocs = ({ concept, additionalFilter, include_sentiment_on_concepts }) => {
    const { id } = useParams();
    const [isVisible, setIsVisible] = useState(false);
    const [searchConcept, SetSearchConcept] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);

    const [llmSummary, setLlmSummary] = useState(null);
    const [customPrompt, setCustomPrompt] = useState('');
    const [documentLimit, setDocumentLimit] = useState(documents.length); // Default to all documents

    const [triggerGetDocuments] = useGetDocumentsMutation();
    const [triggerGetLLMPassThrough] = useGetLLMPassThroughMutation();

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const result = await triggerGetDocuments({
                id,
                filter: additionalFilter ? additionalFilter : [],
                search: concept.concept.texts,
                limit: 50,
                include_sentiment_on_concepts,
            }).unwrap();
            setDocuments(result.documents);
            SetSearchConcept(result.concept);
            setDocumentLimit(result.documents.length); // Update the limit to all documents
        } catch (error) {
            console.error("Failed to fetch documents:", error);
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    }, [triggerGetDocuments, id, additionalFilter, concept.name, include_sentiment_on_concepts]);

    const handleLlmRequest = async () => {
        if (!customPrompt) return;

        const documentTexts = documents.slice(0, documentLimit).map((d) => `<p>${d.text}</p>`).join("");
        const promptLlm = customPrompt + "\n" + documentTexts;

        try {
            const llmResponse = await triggerGetLLMPassThrough({
                projectId: id,
                prompt: promptLlm,
            }).unwrap();
            setLlmSummary(llmResponse.response);
        } catch (error) {
            console.error("Failed to fetch LLM summary:", error);
        }
    };

    const showModal = () => {
        setIsVisible(true);
        fetchDocuments();
    };

    const handleCancel = () => {
        setIsVisible(false);
        setDocuments([]);
        setLlmSummary(null);
    };

    const documentItems = documents.map((doc, index) => {
        const { paragraphList } = getFormattedDocument(
            doc,
            searchConcept,
            false
        );
        return (
            <Card
                key={index}
                title={doc.title || "Document Details"}
                bordered={false}
                style={{
                    width: 400,
                    height: 350,
                    margin: 10,
                }}
            >
                <Scrollbars style={{ height: 250 }} autoHide>
                    <ParagraphList paragraphList={paragraphList} />
                    <MetadataFields metadata={doc.metadata} />
                </Scrollbars>
            </Card>
        );
    });

    const renderSummary = () => {
        if (!llmSummary) return null;

        return (
            <div style={{ marginBottom: '1rem' }}>
                <p>{llmSummary}</p>
            </div>
        );
    };

    return (
        <div>
            <div onClick={showModal} className={"overall-sentiment-concept"}>
                {concept.name}: {concept.count}
            </div>
            <Modal
                width={"80%"}
                title={`Details for "${concept.name}"`}
                open={isVisible}
                onCancel={handleCancel}
                footer={null}
            >
                {loading ? (
                    <Spin
                        indicator={
                            <LoadingOutlined style={{ fontSize: 24 }} spin />
                        }
                    />
                ) : (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Input.TextArea
                            rows={4}
                            value={customPrompt}
                            onChange={(e) => setCustomPrompt(e.target.value)}
                            placeholder="Enter your custom prompt for the AI"
                            style={{ marginBottom: '1rem', width: '100%' }}
                        />
                        <label >Number of documents to include (max - {documents.length}):</label>
                        <Input
                            type="number"
                            min={1}
                            max={documents.length}
                            value={documentLimit}
                            onChange={(e) => setDocumentLimit(Number(e.target.value))}
                            placeholder="Number of documents to include"
                            style={{ marginBottom: '1rem', width: '100%' }}
                        />
                        <Button
                            type="primary"
                            onClick={handleLlmRequest}
                            disabled={!customPrompt || documents.length === 0}
                            style={{ marginBottom: '1rem' }}
                        >
                            Ask AI
                        </Button>
                        {renderSummary()}
                        {documentItems.length > 0 ? (
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                }}
                            >
                                {documentItems}
                            </div>
                        ) : (
                            <p>No documents found.</p>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default SentimentDocs;
